import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ImagePlus, X, Wand2, Mic, MicOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { linkitApi, formatApiErrorDetail, resolveImg } from "../api";
import { useSpeechRecognition } from "../../hooks/useSpeechRecognition";

const EXAMPLES = [
  "I'm a travel photographer. Build a moody dark page with my portfolio gallery, print shop, and Instagram.",
  "Make me a bold, colorful page for my podcast — link my Spotify, YouTube, and a newsletter signup.",
  "I sell handmade jewelry. Warm earthy tones, a product block, my Etsy link, and social icons.",
];

export default function AIAssistant({ open, onClose, onApplied }) {
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [images, setImages] = useState([]); // {url, path}
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const dragDepth = useRef(0);
  const voiceBaseRef = useRef("");

  const voice = useSpeechRecognition({
    onTranscript: ({ text, isFinal }) => {
      const base = voiceBaseRef.current;
      const joined = base ? `${base.trim()} ${text}`.replace(/\s+/g, " ") : text;
      setPrompt(joined);
      if (isFinal) voiceBaseRef.current = joined;
    },
  });
  const toggleVoice = () => {
    if (!voice.listening) voiceBaseRef.current = prompt || "";
    voice.toggle();
  };
  useEffect(() => { if (voice.error) toast.error(voice.error); }, [voice.error]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await linkitApi.post("/ai/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return { url: data.url, path: data.path };
      }));
      setImages((prev) => [...prev, ...results]);
      toast.success(`${results.length} image${results.length > 1 ? "s" : ""} attached`);
    } catch { toast.error("Image upload failed"); } finally { setUploading(false); }
  };

  const attach = (e) => uploadFiles(e.target.files);

  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => { if (e.dataTransfer?.types?.includes("Files")) e.preventDefault(); };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === "file" && it.type.startsWith("image/"));
    if (!items.length) return;
    e.preventDefault();
    uploadFiles(items.map((it) => it.getAsFile()).filter(Boolean));
  };

  const send = async (text) => {
    const p = (text ?? prompt).trim();
    if (!p || busy) return;
    const attached = images;
    setMessages((m) => [...m, { role: "user", text: p, images: attached.map((i) => i.url) }]);
    setPrompt("");
    voiceBaseRef.current = "";
    setImages([]);
    setBusy(true);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    try {
      const { data } = await linkitApi.post("/ai/generate", {
        prompt: p,
        image_paths: attached.map((i) => i.path),
        image_urls: attached.map((i) => i.url),
      });
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
      onApplied(data);
      toast.success("Draft updated — review & publish when ready");
    } catch (e) {
      // Restore prompt + attachments so the user doesn't lose their work.
      setPrompt(p);
      voiceBaseRef.current = p;
      setImages(attached);
      setMessages((m) => [...m, { role: "assistant", text: `${formatApiErrorDetail(e.response?.data?.detail)} — I kept your prompt and images so you can retry.`, error: true }]);
    } finally {
      setBusy(false);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="flex h-[80vh] max-w-lg flex-col overflow-hidden border-white/10 bg-[#0b0c10] p-0 text-white"
        aria-describedby={undefined}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={onPaste}
      >
        <DialogTitle className="sr-only">AI Builder</DialogTitle>
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 z-40 m-2 flex items-center justify-center rounded-2xl border-2 border-dashed border-fuchsia-400 bg-fuchsia-500/15 backdrop-blur-sm">
            <div className="text-center">
              <ImagePlus size={30} className="mx-auto text-fuchsia-300" />
              <p className="mt-1 text-sm font-bold text-fuchsia-200">Drop images to attach</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500"><Sparkles size={16} /></span>
          <div>
            <h2 className="text-sm font-bold">AI Builder</h2>
            <p className="text-[11px] text-white/40">Describe changes — I edit your draft</p>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {messages.length === 0 ? (
            <div className="pt-6 text-center">
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30"><Wand2 size={22} className="text-fuchsia-300" /></span>
              <p className="text-sm font-semibold">Build your page with a sentence</p>
              <p className="mx-auto mt-1 max-w-xs text-xs text-white/40">Tell me who you are and what you want. Attach photos to match your style.</p>
              <div className="mt-5 space-y-2 text-left">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => send(ex)} data-testid={`ai-example-${i}`} className="w-full rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left text-xs text-white/70 transition-colors hover:border-fuchsia-400/40 hover:bg-white/[0.06]">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-indigo-500 text-white" : m.error ? "bg-red-500/15 text-red-300" : "bg-white/[0.06] text-white/90"}`}>
                  {m.images?.length ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {m.images.map((u, idx) => <img key={idx} src={resolveImg(u)} alt="" className="h-12 w-12 rounded-lg object-cover" />)}
                    </div>
                  ) : null}
                  {m.text}
                </div>
              </div>
            ))
          )}
          {busy && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl bg-white/[0.06] px-3.5 py-2.5 text-sm text-white/60">
                <Loader2 size={14} className="animate-spin" /> Designing your page…
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-white/10 p-3">
          {images.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {images.map((im, i) => (
                <div key={i} className="relative">
                  <img src={resolveImg(im.url)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <button onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-2">
            <button onClick={() => fileRef.current?.click()} data-testid="ai-attach" title="Attach images (or drag & drop)" className="shrink-0 rounded-lg p-2 text-white/50 hover:bg-white/5 hover:text-white">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            {voice.supported && (
              <button
                onClick={toggleVoice}
                data-testid="ai-mic"
                title={voice.listening ? "Stop recording" : "Speak your prompt"}
                className={`relative shrink-0 rounded-lg p-2 transition-colors ${voice.listening ? "bg-red-500 text-white" : "text-white/50 hover:bg-white/5 hover:text-white"}`}
              >
                {voice.listening ? <MicOff size={18} /> : <Mic size={18} />}
                {voice.listening && <span className="absolute inset-0 -z-10 animate-ping rounded-lg bg-red-500/40" />}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={attach} />
            <textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); voiceBaseRef.current = e.target.value; }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (voice.listening) voice.stop(); send(); } }}
              rows={1}
              placeholder={voice.listening ? "Listening… speak now" : "Describe your page or a change…"}
              data-testid="ai-prompt"
              className="max-h-28 min-h-[24px] flex-1 resize-none bg-transparent py-1.5 text-sm outline-none placeholder:text-white/30"
            />
            <button onClick={() => { if (voice.listening) voice.stop(); send(); }} disabled={busy || !prompt.trim()} data-testid="ai-send" className="shrink-0 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 p-2 disabled:opacity-40">
              <Send size={16} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
